import { stringifyEventData } from './stringifyEventData'
import {
  SourceHistoryEventData,
  getSourceHistoryEventData,
} from './sourceHistory'
import {
  TrackingProduct,
  TrackingProductCategory,
  TrackingElementContext,
  TrackingSectionContext,
  TrackingVariant,
} from '../../..//types'
import {
  DataLayerProductGA3,
  TrackingClientState,
  getFilterString,
  DataLayerProductGA4,
} from './clientState'
import {
  calcCentsToEuro,
  getIsInSale,
  getTrackingPriceDataFromProduct,
} from './prices'
import { getSourceCategory } from './category'

export const productDimensionMapping: Record<string, string> = {
  productAvailability: 'dimension53',

  merchantId: 'dimension67',
  categoryId: 'dimension66',
  sourceCategory: 'dimension68',
  sourceCategoryId: 'dimension69',

  strikeOutPrice: 'dimension114',

  sectionIndexOnPage: 'dimension106',
  rowIndexInSection: 'dimension108',
  columnIndexInSection: 'dimension110',

  isInSale: 'dimension70',
  saleAmount: 'dimension71',
  isSoldOut: 'dimension113',

  initiallyVisibleColumns: 'dimension73',
  source: 'dimension74',
  sourceHistoryOne: 'dimension75',
  sourceHistoryTwo: 'dimension76',
  sourceHistoryThree: 'dimension77',

  destinationUrl: 'dimension78',

  fitAnalyticsUsed: 'dimension54',
  sort: 'dimension55',
  filter: 'dimension112',

  masterKey: 'dimension119',
  sponsored: 'dimension120',
  supplierId: 'dimension121',
  sizeLabel: 'dimension122',

  price_bi: 'dimension26',
}

export type MappedDimensionsProduct<T> = {} & {
  id: number
  sizeLabel: string
  price: number
  readonly ____tagMappedDimensionsProductT: T
}

type ProductTrackingContextType =
  | { element: TrackingElementContext; section: TrackingSectionContext }
  | { element: undefined; section: undefined }

type OptionalTrackingProductCategory =
  | TrackingProductCategory
  | {
      path: string | null
      id: number | null
    }

function mapProductDimensions<T extends {}>(p: T): MappedDimensionsProduct<T> {
  const result: { [k: string]: unknown } = {}

  for (const key of Object.keys(p)) {
    const newKey: string = productDimensionMapping[key] || key

    result[newKey] = (p as { [k: string]: unknown })[key]
  }

  return result as MappedDimensionsProduct<T>
}
// eslint-disable-next-line complexity
export const createDataLayerProduct = ({
  product,
  quantity = 1,
  variantId,
  category,
  sourceCategory,
  price,
  strikeOutPrice,
  tax,
  saleAmount,
  positionData = {
    position: 1,
    sectionIndexOnPage: 1,
    rowIndexInSection: 1,
    columnIndexInSection: 1,
  },
  sourceHistory,
  sort,
  sortChannel,
  filter,
  fitAnalyticsUsed,
  supplierId,
  sizeLabel,
}: {
  product: TrackingProduct
  quantity?: number
  variantId?: number
  category: OptionalTrackingProductCategory | null
  sourceCategory: OptionalTrackingProductCategory | null
  price: number
  strikeOutPrice: string | null
  tax: number
  saleAmount: number
  positionData?: {
    position: number
    sectionIndexOnPage: number
    rowIndexInSection: number
    columnIndexInSection: number
    initiallyVisibleColumns?: number
  }
  sourceHistory: SourceHistoryEventData
  sort: string
  sortChannel?: string
  filter: string
  fitAnalyticsUsed: string
  supplierId?: number
  sizeLabel?: string
}): {
  dataLayerProductGA3: MappedDimensionsProduct<DataLayerProductGA3>
  dataLayerProductGA4: DataLayerProductGA4
} => {
  const placement = sourceHistory.source?.split('|')[3] ?? ''

  const priceTracker = product.price?.tracker
  // price_bi data order: <display_price>|<sale_discount>|<campaign_discount>|<original_price>|<voucherReduction>|<couponReduction>
  const price_bi = stringifyEventData([
    product.price?.price.amount
      ? calcCentsToEuro(product.price?.price.amount)
      : (product.minPrice.withTax / 100).toFixed(2),
    (product.minPrice.saleReductionWithTax / 100).toFixed(2),
    (product.minPrice.campaignReductionWithTax / 100).toFixed(2),
    strikeOutPrice || (product.minPrice.withTax / 100).toFixed(2),
    calcCentsToEuro(priceTracker?.voucherReduction ?? 0),
    calcCentsToEuro(priceTracker?.couponReduction ?? 0),
  ])

  const dataLayerProductGA3 = mapProductDimensions<DataLayerProductGA3>({
    id: product.id,
    name: stringifyEventData([
      product.id,
      product.name,
      product.brand.id,
      product.brand.name,
    ]),
    brand: stringifyEventData([product.brand.id, product.brand.name]),
    quantity,
    variant: variantId ?? null,

    category: category && category.path,
    categoryId: category && category.id,
    sourceCategory: sourceCategory && sourceCategory.path,
    sourceCategoryId: sourceCategory && sourceCategory.id,

    price: price.toFixed(2),
    strikeOutPrice,
    tax: tax.toFixed(2),

    ...positionData,

    isInSale: getIsInSale({ productPrice: product.minPrice }),
    saleAmount: saleAmount.toFixed(2),
    isSoldOut: product.isSoldOut,

    ...sourceHistory,

    destinationUrl: product.url,

    sponsored: stringifyEventData([
      sortChannel,
      product.isSponsored,
      product.sponsoredType || '',
    ]),

    sort,
    filter,
    fitAnalyticsUsed,

    ...(supplierId ? { supplierId } : {}),
    ...(sizeLabel ? { sizeLabel } : {}),
    masterKey: product.masterKey,
    merchantId: product.merchantId ? product.merchantId : null,
    price_bi,
  })

  const dataLayerProductGA4: DataLayerProductGA4 = {
    item_id: String(product.id),
    item_name: product.name,
    price: Number(price.toFixed(2)),
    currency: product.minPrice.currency,
    sale_discount: Number(saleAmount.toFixed(2)),
    campaign_discount: Number(
      (product.minPrice.campaignReductionWithTax / 100).toFixed(2),
    ),
    original_price: strikeOutPrice ? Number(strikeOutPrice) : null,
    price_bi,
    sold_out: product.isSoldOut,
    item_brand: product.brand.name,
    item_brand_id: product.brand.id.toString(),
    item_category: category && category.path,
    item_category_id: category && category.id,
    item_variant: variantId ? String(variantId) : '',
    quantity,
    item_list_name: '',
    item_list_id: '',
    index: positionData.position,

    sponsored: stringifyEventData([
      sortChannel,
      product.isSponsored,
      product.sponsoredType || '',
    ]),
    tax: Number(tax.toFixed(2)),

    sort,
    filter,
    // TBD
    fit_analytics_used: fitAnalyticsUsed,

    ...(supplierId ? { supplier_id: String(supplierId) } : {}),
    ...(sizeLabel ? { size_label: sizeLabel } : {}),
    master_key: product.masterKey,
    merchant_id: product.merchantId ? product.merchantId : null,
    position_bi: stringifyEventData([
      positionData.sectionIndexOnPage || 0,
      positionData.rowIndexInSection || 0,
      positionData.columnIndexInSection || 0,
      positionData.initiallyVisibleColumns || 0,
    ]),
    source: sourceHistory.source,
    location_id: placement,
    destination: stringifyEventData(['adp', product.id]),
    destination_url: product.url,
    linktarget_id: stringifyEventData(['adp', product.id]),
    linktarget: product.url,
  }

  return {
    dataLayerProductGA3,
    dataLayerProductGA4,
  }
}

export function getStateFullDataLayerProduct({
  currentState,
  variant,
  product,
  element,
  section,
  quantity,
  sortChannel,
  category,
}: {
  currentState: TrackingClientState
  variant?: TrackingVariant
  product: TrackingProduct
  quantity?: number
  sortChannel?: string | undefined
  category: OptionalTrackingProductCategory
} & ProductTrackingContextType) {
  const sourceCategory = getSourceCategory(currentState)

  const { price, saleAmount, strikeOutPrice, tax } =
    getTrackingPriceDataFromProduct(variant ? variant.price : product.minPrice)

  const positionDataObject = getPosition({
    element,
    section,
  } as ProductTrackingContextType)

  const { dataLayerProductGA3, dataLayerProductGA4 } = createDataLayerProduct({
    category,
    filter: getFilterString(currentState),
    fitAnalyticsUsed: currentState.fitAnalyticsUsed,
    positionData: positionDataObject,
    price,
    product,
    quantity,
    saleAmount,
    sort: currentState.sort,
    sortChannel,
    sourceCategory,
    sourceHistory: getSourceHistoryEventData({
      state: currentState,
      currentSourceSection: section,
    }),
    strikeOutPrice,
    tax,
    variantId: variant?.id,
  })

  return {
    productGA3: dataLayerProductGA3,
    productGA4: dataLayerProductGA4,
  }
}

function getPosition({ element, section }: ProductTrackingContextType) {
  if (element !== undefined && section !== undefined) {
    return {
      position:
        element.type === 'element-context'
          ? element.elementIndexInSection + 1
          : element.productIndexOnPage + 1,
      sectionIndexOnPage: section.sectionIndexOnPage + 1,
      rowIndexInSection: element.rowIndexInSection + 1,
      columnIndexInSection: element.columnIndexInSection + 1,

      initiallyVisibleColumns:
        section.type === 'reco' ? section.initiallyVisibleColumns : 1,
    }
  } else {
    return {
      position: 1,
      sectionIndexOnPage: 1,
      rowIndexInSection: 1,
      columnIndexInSection: 1,
    }
  }
}
